import { useEffect, useMemo, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Modal } from '@shared/ui/Modal';
import { Button } from '@shared/ui/Button';
import { Field, Input, Label, Select } from '@shared/ui/Input';
import { ACCEPT_DOCX_ONLY } from '@shared/lib/fileAccept';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { IconFile } from '@shared/ui/icons';
import {
  useCreateLawsuitMutation,
  useComposeLawsuitMutation,
  useListClaimsQuery,
  useListContractsQuery,
  useGetContractQuery,
} from '@shared/api';

/**
 * Виджет создания иска (Lawsuit) ADR-0001.
 *
 * Флоу:
 *   1. Юрист задаёт название иска.
 *   2. Источник претензии: существующая Claim (селект) ИЛИ загрузка DOCX.
 *   3. Если выбрана претензия с привязанным `contract_id` — договор
 *      определяется автоматически и отображается read-only (запрашиваем
 *      getContract для подписи). Иначе юрист выбирает существующий
 *      договор / грузит DOCX / оставляет без договора.
 *   4. Submit: POST /lawsuits (multipart) → POST /lawsuits/{id}/compose.
 *      Сразу закрываем модалку и переходим на карточку — там видно
 *      статус 'composing' со спиннером.
 *
 * Ошибки 4xx (например, файл не .docx) показываем inline над кнопкой
 * сабмита, модалка остаётся открытой — юрист исправит и повторит.
 */

const Note = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const RadioGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(2)};

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;


const RadioOption = styled.label<{ $active: boolean }>`
  display: block;
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primarySoft : 'transparent'};
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.text.primary};
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  & > input {
    /* Скрываем нативный radio — фокус-стейт делегируем :focus-within ниже. */
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  & > span {
    display: block;
    margin-top: 4px;
    color: ${({ theme }) => theme.colors.text.muted};
    font-size: 12px;
    font-weight: 400;
  }

  &:focus-within {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 13px;
`;

const FileName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
`;

const Resolved = styled.div`
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;

  & > strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

const ErrorBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.status.danger}14;
  border-left: 3px solid ${({ theme }) => theme.colors.status.danger};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  line-height: 1.45;
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(5)};
  flex-wrap: wrap;
  justify-content: flex-end;
`;

type ClaimSource = 'existing' | 'file';
// Договор обязателен (compose_lawsuit fail'ится без текста договора в
// resolve_contract_text). Если у выбранной претензии нет contract_id —
// юрист обязан выбрать договор из реестра или загрузить DOCX.
type ContractSource = 'existing' | 'file';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Колбэк после успешной постановки в очередь. id — свежесозданный иск. */
  onCreated: (lawsuitId: string) => void;
}

function extractApiError(err: unknown): string {
  if (err && typeof err === 'object' && 'data' in err) {
    const data = (err as { data?: { error?: { message?: string } } }).data;
    if (data?.error?.message) return data.error.message;
  }
  return 'Не удалось создать иск. Попробуйте ещё раз.';
}

function formatContractLabel(c: {
  counterparty: string;
  subject: string | null;
}): string {
  return c.subject ? `${c.counterparty} — ${c.subject}` : c.counterparty;
}

export function CreateLawsuitModal({ open, onClose, onCreated }: Props) {
  const [title, setTitle] = useState('');
  const [claimSource, setClaimSource] = useState<ClaimSource>('existing');
  const [claimId, setClaimId] = useState('');
  const [claimFile, setClaimFile] = useState<File | null>(null);

  const [contractSource, setContractSource] = useState<ContractSource>('existing');
  const [contractId, setContractId] = useState('');
  const [contractFile, setContractFile] = useState<File | null>(null);

  const [error, setError] = useState<string | null>(null);

  const { data: claimsData } = useListClaimsQuery({ limit: 200 });
  const { data: contractsData } = useListContractsQuery();

  // Если выбрана существующая претензия с привязанным contract_id —
  // подтягиваем договор и блокируем выбор. Это симметрично back-валидации:
  // user_input.contract_id игнорируется, если claim.contract_id != null.
  const selectedClaim = useMemo(
    () => claimsData?.items.find((c) => c.id === claimId) ?? null,
    [claimsData, claimId],
  );
  const autoContractId = selectedClaim?.contract_id ?? null;

  const { data: autoContract } = useGetContractQuery(autoContractId ?? '', {
    skip: !autoContractId,
  });

  const [createLawsuit, { isLoading: isCreating }] = useCreateLawsuitMutation();
  const [composeLawsuit, { isLoading: isComposing }] = useComposeLawsuitMutation();
  const isSubmitting = isCreating || isComposing;

  useEffect(() => {
    if (!open) {
      setTitle('');
      setClaimSource('existing');
      setClaimId('');
      setClaimFile(null);
      setContractSource('existing');
      setContractId('');
      setContractFile(null);
      setError(null);
    }
  }, [open]);

  // При смене источника претензии — сбрасываем подвалу-привязку, иначе
  // авто-договор останется приклеенным.
  useEffect(() => {
    if (claimSource === 'file') {
      setClaimId('');
    } else {
      setClaimFile(null);
    }
  }, [claimSource]);

  // Подтянули претензию с claim.contract_id != null — переключаемся в режим
  // 'existing' и выставляем contractId. Юрист не сможет сменить — это
  // соответствует back-валидации.
  useEffect(() => {
    if (autoContractId) {
      setContractSource('existing');
      setContractId(autoContractId);
    }
  }, [autoContractId]);

  function validate(): string | null {
    if (!title.trim()) return 'Укажите название иска.';
    if (claimSource === 'existing' && !claimId) {
      return 'Выберите исходную претензию из списка.';
    }
    if (claimSource === 'file' && !claimFile) {
      return 'Загрузите файл претензии (.docx).';
    }
    if (claimSource === 'file' && claimFile) {
      const name = claimFile.name.toLowerCase();
      if (!name.endsWith('.docx')) {
        return 'Файл претензии должен быть в формате DOCX.';
      }
    }
    // Договор обязателен — либо автоматически подтянут из выбранной
    // претензии, либо юрист указывает явно (id из реестра или DOCX).
    if (!autoContractId) {
      if (contractSource === 'existing' && !contractId) {
        return 'Выберите договор из списка или загрузите файл договора (.docx).';
      }
      if (contractSource === 'file' && !contractFile) {
        return 'Загрузите файл договора (.docx).';
      }
      if (contractSource === 'file' && contractFile) {
        const name = contractFile.name.toLowerCase();
        if (!name.endsWith('.docx')) {
          return 'Файл договора должен быть в формате DOCX.';
        }
      }
    }
    return null;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    const form = new FormData();
    form.append('title', title.trim());
    if (claimSource === 'existing') {
      form.append('claim_id', claimId);
    } else if (claimFile) {
      form.append('claim_file', claimFile);
    }
    // Если контрагент claim уже определён — приоритет за ним (back игнорирует
    // переданное вручную). Иначе пытаемся передать ручной выбор юриста.
    if (autoContractId) {
      form.append('contract_id', autoContractId);
    } else if (contractSource === 'existing' && contractId) {
      form.append('contract_id', contractId);
    } else if (contractSource === 'file' && contractFile) {
      form.append('contract_file', contractFile);
    }

    try {
      const created = await createLawsuit(form).unwrap();
      await composeLawsuit(created.id).unwrap();
      onCreated(created.id);
    } catch (err) {
      setError(extractApiError(err));
    }
  }

  return (
    <Modal open={open} title="Создать исковое заявление" onClose={onClose}>
      <Note>
        Агент составит проект иска по корпоративному шаблону, опираясь на
        текст претензии и условия договора. Через 30–60 секунд DOCX будет
        доступен для скачивания.
      </Note>

      <form onSubmit={onSubmit} noValidate>
        <Field>
          <Label htmlFor="lawsuit-title">Название иска *</Label>
          <Input
            id="lawsuit-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="напр., «Иск к ООО „Подряд“ по договору № 12 от 03.02.2026»"
            required
            maxLength={200}
          />
        </Field>

        <Field>
          <Label>Исходная претензия *</Label>
          <RadioGroup>
            <RadioOption $active={claimSource === 'existing'}>
              <input
                type="radio"
                name="claim-source"
                value="existing"
                checked={claimSource === 'existing'}
                onChange={() => setClaimSource('existing')}
              />
              <strong>Из реестра претензий</strong>
              <span>Выбрать существующую запись со всей фактурой</span>
            </RadioOption>
            <RadioOption $active={claimSource === 'file'}>
              <input
                type="radio"
                name="claim-source"
                value="file"
                checked={claimSource === 'file'}
                onChange={() => setClaimSource('file')}
              />
              <strong>Загрузить файл</strong>
              <span>Если претензии нет в реестре — DOCX отдельным файлом</span>
            </RadioOption>
          </RadioGroup>
        </Field>

        {claimSource === 'existing' ? (
          <Field>
            <Label htmlFor="claim-id">Претензия</Label>
            <Select
              id="claim-id"
              value={claimId}
              onChange={(e) => setClaimId(e.target.value)}
              required
            >
              <option value="">— выберите претензию —</option>
              {(claimsData?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.counterparty}
                  {c.dispute_type ? ` — ${c.dispute_type}` : ''}
                  {c.amount != null
                    ? ` · ${c.amount.toLocaleString('ru-RU')} ₽`
                    : ''}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          <Field>
            <Label>Файл претензии (.docx)</Label>
            {!claimFile ? (
              <FileDropZone
                accept={ACCEPT_DOCX_ONLY}
                hint="DOCX с текстом претензии — до 25 МБ. Старый .doc сконвертируем сами."
                onFile={(f) => setClaimFile(f)}
              />
            ) : (
              <FileRow>
                <FileName>
                  <IconFile size={16} />
                  {claimFile.name}
                </FileName>
                <Button
                  type="button"
                  $variant="secondary"
                  $size="sm"
                  onClick={() => setClaimFile(null)}
                >
                  Удалить
                </Button>
              </FileRow>
            )}
          </Field>
        )}

        <Field>
          <Label>Договор</Label>
          {autoContractId ? (
            <Resolved>
              Определён автоматически по претензии:{' '}
              {autoContract ? (
                <strong>{formatContractLabel(autoContract)}</strong>
              ) : (
                <strong>загружается…</strong>
              )}
            </Resolved>
          ) : (
            <>
              <RadioGroup>
                <RadioOption $active={contractSource === 'existing'}>
                  <input
                    type="radio"
                    name="contract-source"
                    value="existing"
                    checked={contractSource === 'existing'}
                    onChange={() => setContractSource('existing')}
                  />
                  <strong>Из реестра</strong>
                  <span>Выбрать договор</span>
                </RadioOption>
                <RadioOption $active={contractSource === 'file'}>
                  <input
                    type="radio"
                    name="contract-source"
                    value="file"
                    checked={contractSource === 'file'}
                    onChange={() => setContractSource('file')}
                  />
                  <strong>Файл .docx</strong>
                  <span>Загрузить вручную</span>
                </RadioOption>
              </RadioGroup>

              {contractSource === 'existing' && (
                <div style={{ marginTop: 12 }}>
                  <Select
                    aria-label="Договор"
                    value={contractId}
                    onChange={(e) => setContractId(e.target.value)}
                  >
                    <option value="">— выберите договор —</option>
                    {(contractsData?.items ?? []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {formatContractLabel(c)}
                      </option>
                    ))}
                  </Select>
                </div>
              )}

              {contractSource === 'file' && (
                <div style={{ marginTop: 12 }}>
                  {!contractFile ? (
                    <FileDropZone
                      accept={ACCEPT_DOCX_ONLY}
                      hint="DOCX договора — до 25 МБ. Старый .doc сконвертируем сами."
                      onFile={(f) => setContractFile(f)}
                    />
                  ) : (
                    <FileRow>
                      <FileName>
                        <IconFile size={16} />
                        {contractFile.name}
                      </FileName>
                      <Button
                        type="button"
                        $variant="secondary"
                        $size="sm"
                        onClick={() => setContractFile(null)}
                      >
                        Удалить
                      </Button>
                    </FileRow>
                  )}
                </div>
              )}
            </>
          )}
        </Field>

        {error && <ErrorBox role="alert">{error}</ErrorBox>}

        <Actions>
          <Button type="button" $variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Отмена
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Создаём…' : 'Создать и составить'}
          </Button>
        </Actions>
      </form>
    </Modal>
  );
}
